import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Tooltip,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import InPlaceEditableEmailPreviewMAIQ from 'app/components/InPlaceEditableEmailPreviewMAIQ';
import { styled } from '@mui/material/styles';
import { 
  Save, 
  Close, 
  Email, 
  Check, 
  Refresh,
  CheckCircle,
  Error,
  ListAlt,
  Edit,
  Visibility,
  Download,
} from '@mui/icons-material';
import { EmailPreviewProps} from '../../../types';
import ModalMAIQ from 'app/components/ModalMAIQ';
import DownloadModalMAIQ from 'app/components/DownloadModalMAIQ';
import Step1Container from 'app/pages/EmailCreateEditPage/component/Step1Container';
import { useEmailTemplate } from 'app/context/EmailTemplateContext';
import { EmailTemplate } from 'app/apiService/emailTemplate/emailTemplateApiService';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { useHistory } from 'react-router-dom';
import { PreviewCard } from './PreviewCard';
import TabsMAIQ from 'app/components/TabsMAIQ';

const PreviewContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  overflowY: 'auto',
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(0, 2, 2, 2),
}));


const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(0, 0, 2, 0),
  marginBottom: theme.spacing(4),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HeaderIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  backgroundColor: theme.palette.secondary.main,
  color: 'white',
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  flex: 1,
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
}));

const HeaderSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.secondary,
  fontWeight: 400,
}));








// Enhanced InPlaceEditableEmailPreview Component with full editing capabilities



const EmailContentPreview: React.FC<EmailPreviewProps> = ({
  emailContent,
  step,
  onPreview,
  onEdit,
  onSave,
  showPreview,
  onClosePreview,
  onShowEditableEmail,
  showEditableEmail,
  onCloseEditableEmail,
  onEmailContentChange,
  onGeneratedEmailChange,
  isGenerating = false, // Add generating status prop with default value
  isGeneratingEmail = false, // Add email generation status prop with default value
  generatedEmail = null, // Add generated email prop with default value
  streamingData = null, // Add streaming data prop with default value
  streamingText = '', // Add streaming text prop with default value
  currentSection = null, // Add current section prop with default value
  conversationId = '', // Add conversation ID prop with default value
  onTemplateSaveSuccess, // Callback after template save success
}) => {
  // Email template context
  const { createEmailTemplate, loading: templateLoading, error: templateError } = useEmailTemplate();
  
  // Get selected dealer
  const selectedDealer = useSelector(selectDealerSelected) as any;
  
  const selectedDealerId = selectedDealer?.dealer || '';
  
  // Router history for navigation
  const history = useHistory();
  
  
  // State for save template modal
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  
  // State for template form
  const [templateState, setTemplateState] = useState({
    type: 'DEALER_VALUE',
    name: '',
    subject: '',
    html: '',
  });
  const [templateFormError, setTemplateFormError] = useState({});
  
  // State for notifications
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  
  // State for result modal (success/error)
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultModalData, setResultModalData] = useState<{
    type: 'success' | 'error';
    title: string;
    message: string;
    templateName?: string;
  }>({
    type: 'success',
    title: '',
    message: '',
  });

  // State for in-place editing
  const [editableHtmlContent, setEditableHtmlContent] = useState('');
  const [originalGeneratedHtml, setOriginalGeneratedHtml] = useState('');
  
  // State to preserve the original generated email state
  const [originalGeneratedEmailState, setOriginalGeneratedEmailState] = useState<any>(null);
  
  // State for tab management
  const [activeTab, setActiveTab] = useState(0);
  
  // State for edit/preview toggle in Generated Email tab
  const [isEditModeInGeneratedTab, setIsEditModeInGeneratedTab] = useState(false);
  
  // State for download modal
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [downloadFileName, setDownloadFileName] = useState('');
  
  // Effect to preserve the original generated email state when it's first created
  useEffect(() => {
    if (generatedEmail && !originalGeneratedEmailState) {
      // Store the original generated email state (deep copy)
      setOriginalGeneratedEmailState({
        ...generatedEmail,
        html: generatedEmail.html,
        subject: generatedEmail.subject,
      });
      setOriginalGeneratedHtml(generatedEmail.html);
      
      // Automatically switch to Generated Email tab when email is first generated
      setActiveTab(1);
    }
  }, [generatedEmail, originalGeneratedEmailState]);

  // Handle content change for in-place editing
  const handleContentChange = useCallback((sectionId: string, newContent: string) => {
    if (onEmailContentChange) {
      // Update the specific section content
      const updatedSections = emailContent.sections.map(section => 
        section.id === sectionId 
          ? { ...section, content: newContent }
          : section
      );
      
      const updatedEmailContent = {
        ...emailContent,
        sections: updatedSections
      };
      
      onEmailContentChange(updatedEmailContent);
    }
  }, [emailContent, onEmailContentChange]);

  // Handle HTML content change
  const handleHtmlChange = useCallback((newHtml: string) => {
    setEditableHtmlContent(newHtml);
    
    // Always update the generatedEmail state immediately when HTML changes
    if (generatedEmail && onGeneratedEmailChange) {
      onGeneratedEmailChange({
        ...generatedEmail,
        html: newHtml
      });
    }
  }, [generatedEmail, onGeneratedEmailChange]);

  // Handle reset to original HTML
  const handleResetToOriginal = useCallback(() => {
    if (originalGeneratedEmailState) {
      // Reset to the original generated email state
      setEditableHtmlContent(originalGeneratedEmailState.html);
      
      // Also update the generatedEmail state to original
      if (generatedEmail && onGeneratedEmailChange) {
        onGeneratedEmailChange({
          ...generatedEmail,
          html: originalGeneratedEmailState.html,
          subject: originalGeneratedEmailState.subject
        });
      }
      
      // Update the editableHtmlContent state as well
      setOriginalGeneratedHtml(originalGeneratedEmailState.html);
    }
  }, [originalGeneratedEmailState, generatedEmail, onGeneratedEmailChange]);

  // Handle save template click
  const handleSaveTemplateClick = useCallback(() => {
    // Always use the most current generatedEmail state since handleHtmlChange updates it immediately
    let currentHtml = '';
    let currentSubject = '';
    
    if (generatedEmail?.html) {
      // Use the current generated email state (which is updated in real-time by handleHtmlChange)
      currentHtml = generatedEmail.html;
      currentSubject = generatedEmail.subject || emailContent.subject || '';
    } else {
      // Fallback to emailContent if no generated email
      currentHtml = '';
      currentSubject = emailContent.subject || '';
    }
    
    // Populate template state with current email data
    setTemplateState({
      type: 'DEALER_VALUE', // Default type, can be made dynamic
      name: `Generated Email - ${new Date().toLocaleDateString()}`,
      subject: currentSubject,
      html: currentHtml,
    });
    setTemplateFormError({});
    setShowSaveTemplateModal(true);
  }, [generatedEmail, emailContent]);

  // Handle close save template modal
  const handleCloseSaveTemplateModal = useCallback(() => {
    setShowSaveTemplateModal(false);
    setTemplateFormError({});
  }, []);

  // Validate template form
  const validateTemplateForm = useCallback(() => {
    const errors: any = {};
    
    if (!templateState.name?.trim()) {
      errors.name = 'Template name is required';
    }
    
    if (!templateState.subject?.trim()) {
      errors.subject = 'Subject is required';
    }
    
    if (!templateState.html?.trim()) {
      errors.html = 'HTML content is required';
    }
    
    if (!templateState.type?.trim()) {
      errors.type = 'Template type is required';
    }
    
    setTemplateFormError(errors);
    return Object.keys(errors).length === 0;
  }, [templateState]);

  // Handle save template
  const handleSaveTemplate = useCallback(async () => {
    if (!selectedDealerId) {
      setResultModalData({
        type: 'error',
        title: 'Dealer Not Selected',
        message: 'Please select a dealer first before saving the template.',
      });
      setShowResultModal(true);
      return;
    }

    if (!validateTemplateForm()) {
      setResultModalData({
        type: 'error',
        title: 'Validation Error',
        message: 'Please fill in all required fields before saving the template.',
      });
      setShowResultModal(true);
      return;
    }

    try {
      const savedTemplate: EmailTemplate | undefined = await createEmailTemplate(selectedDealerId, {
        type: templateState.type,
        name: templateState.name,
        subject: templateState.subject,
        html: templateState.html,
      });

      // Try to get template ID from different possible fields (id, _id, template_id)
      const templateId = savedTemplate?.id || (savedTemplate as any)?._id || (savedTemplate as any)?.template_id;

      // Call the callback to trigger PUT API and enable chat inputs
      if (onTemplateSaveSuccess && templateId && conversationId) {
        await onTemplateSaveSuccess(templateId, conversationId);
      }

      setResultModalData({
        type: 'success',
        title: 'Template Saved Successfully!',
        message: `Your email template "${templateState.name}" has been saved successfully.`,
        templateName: templateState.name,
      });
      setShowResultModal(true);
      handleCloseSaveTemplateModal();
    } catch (error: any) {
      console.error('Error saving template:', error);
      setResultModalData({
        type: 'error',
        title: 'Save Failed',
        message: error?.message || 'Failed to save email template. Please try again.',
      });
      setShowResultModal(true);
    }
  }, [selectedDealerId, templateState, validateTemplateForm, createEmailTemplate, handleCloseSaveTemplateModal, onTemplateSaveSuccess, conversationId]);

  // Handle notification close
  const handleNotificationClose = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  // Handle result modal close
  const handleResultModalClose = useCallback(() => {
    setShowResultModal(false);
  }, []);

  // Handle redirect to templates page
  const handleRedirectToTemplates = useCallback(() => {
    setShowResultModal(false);
    history.push('/emails-grid');
  }, [history]);

  // Handle tab change
  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  }, []);

  // Handle edit/preview toggle in Generated Email tab
  const handleEditPreviewToggle = useCallback(() => {
    if (isEditModeInGeneratedTab) {
      // Switching to preview mode - ensure any pending changes are saved
      const editableContainer = document.querySelector('#editable-html-container') as HTMLElement;
      if (editableContainer) {
        const currentHtml = editableContainer.innerHTML;
        setEditableHtmlContent(currentHtml);
        
        // Update the generated email state with current edited content
        if (currentHtml && generatedEmail && onGeneratedEmailChange) {
          onGeneratedEmailChange({
            ...generatedEmail,
            html: currentHtml
          });
        }
      }
    } else {
      // Switching to edit mode - use current generated email HTML
      if (generatedEmail?.html) {
        setEditableHtmlContent(generatedEmail.html);
      } else {
        // Fallback to generating HTML from sections
        const htmlFromSections = emailContent.sections
          .map(section => {
            const variant = section.variant || 'default';
            const title = section.title || section.id;
            const content = section.content || '';
            
            if (variant === 'default') {
              return `<div class="email-section" data-section-id="${section.id}" data-section-variant="${variant}">
                <div class="section-content">${content}</div>
              </div>`;
            } else {
              return `<div class="email-section highlight-section" data-section-id="${section.id}" data-section-variant="${variant}">
                <div class="section-title" data-section-id="${section.id}" data-content-type="title">${title}</div>
                <div class="section-content" data-section-id="${section.id}" data-content-type="content">${content}</div>
              </div>`;
            }
          })
          .join('');
        setEditableHtmlContent(htmlFromSections);
      }
    }
    setIsEditModeInGeneratedTab(!isEditModeInGeneratedTab);
  }, [isEditModeInGeneratedTab, generatedEmail, emailContent, onGeneratedEmailChange]);

  // Handle download generated email as HTML file using DownloadMAIQ
  const handleDownloadEmail = useCallback(() => {
    if (!generatedEmail?.html) {
      setNotification({
        open: true,
        message: 'No generated email content available to download.',
        severity: 'error',
      });
      return;
    }

    try {
      // Get dealer name - clean it for filename
      const dealerName = selectedDealer?.dealer_name || selectedDealer?.name || 'Dealer';
      const cleanDealerName = dealerName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '') // Remove special characters
        .replace(/\s+/g, ''); // Remove spaces

      // Generate timestamp in format: YYYY-MM-DD_HH-MM-SS
      const now = new Date();
      const timestamp = now.toISOString()
        .replace(/[-:]/g, '') // Remove dashes and colons
        .replace(/\.\d{3}Z$/, '') // Remove milliseconds and Z
        .replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/, '$1-$2-$3_$4-$5-$6');

      // Create filename: dealername-generatedemail-YYYY-MM-DD_HH-MM-SS.html
      const filename = `${cleanDealerName}-generatedemail-${timestamp}.html`;

      // Create blob with HTML content
      const blob = new Blob([generatedEmail.html], { type: 'text/html;charset=utf-8' });
      
      // Create blob URL for DownloadMAIQ
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Set state for DownloadMAIQ modal
      setDownloadUrl(blobUrl);
      setDownloadFileName(filename);
      setShowDownloadModal(true);

    } catch (error: any) {
      console.error('Error preparing email download:', error);
      setNotification({
        open: true,
        message: 'Failed to prepare email for download. Please try again.',
        severity: 'error',
      });
    }
  }, [generatedEmail, selectedDealer]);

  // Handle close download modal
  const handleCloseDownloadModal = useCallback(() => {
    setShowDownloadModal(false);
    // Clean up blob URL when modal closes
    if (downloadUrl) {
      window.URL.revokeObjectURL(downloadUrl);
      setDownloadUrl('');
      setDownloadFileName('');
    }
  }, [downloadUrl]);


  return (
    <PreviewContainer>
      {/* Section Header */}
      <SectionHeader>
        <HeaderIcon>
          <Email sx={{ fontSize: '24px' }} />
        </HeaderIcon>
        <HeaderContent>
          <HeaderTitle>Email Content Preview</HeaderTitle>
          <HeaderSubtitle>
            {isGeneratingEmail ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="span"
                  sx={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: 'secondary.main',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                      '100%': { opacity: 1 },
                    },
                  }}
                />
                Generating email...
              </Box>
            ) : isGenerating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="span"
                  sx={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: 'secondary.main',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                      '100%': { opacity: 1 },
                    },
                  }}
                />
                Generating email content...
              </Box>
            ) : generatedEmail ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="span"
                  sx={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: 'success.main',
                  }}
                />
                Email generated successfully! Switch to "Generated Email" tab to preview and edit.
              </Box>
            ) : (
              'Review and customize your AI-generated email content sections'
            )}
          </HeaderSubtitle>
        </HeaderContent>
      </SectionHeader>

      {/* Tab Structure */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        width: '100%',
        height: '89%'
      }}>
        {/* Custom Tab Headers */}
        <Box sx={{ 
          display: 'flex', 
          borderBottom: 1, 
          borderColor: 'divider',
          flexShrink: 0,
          width: '100%',
          backgroundColor: 'background.paper',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <Box
            onClick={() => setActiveTab(0)}
            sx={{
              flex: generatedEmail ? 1 : 'none',
              py: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: activeTab === 0 ? 600 : 400,
              color: activeTab === 0 ? 'secondary.main' : 'text.secondary',
              borderBottom: activeTab === 0 ? 3 : 0,
              borderBottomColor: activeTab === 0 ? 'secondary.main' : 'transparent',
              backgroundColor: activeTab === 0 ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '48px',
              transition: 'all 0.3s ease',
              position: 'relative',
              '&:hover': {
                backgroundColor: activeTab === 0 ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.06)',
              }
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 'inherit' }}>
              Content Preview
            </Typography>
          </Box>
          {generatedEmail && (
            <Box
              onClick={() => setActiveTab(1)}
              sx={{
                flex: 1,
                py: 2,
                px: 3,
                textTransform: 'none',
                fontWeight: activeTab === 1 ? 600 : 400,
                color: activeTab === 1 ? 'secondary.main' : 'text.secondary',
                borderBottom: activeTab === 1 ? 3 : 0,
                borderBottomColor: activeTab === 1 ? 'secondary.main' : 'transparent',
                backgroundColor: activeTab === 1 ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '48px',
                transition: 'all 0.3s ease',
                position: 'relative',
                '&:hover': {
                  backgroundColor: activeTab === 1 ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.06)',
                }
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 'inherit' }}>
                Generated Email
              </Typography>
            </Box>
          )}
        </Box>

        {/* Tab Content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'hidden',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {activeTab === 0 && (
            <Box sx={{ 
              height: '100%', 
              overflow: 'auto',
              width: '100%',
              flex: 1
            }}>
              <PreviewCard
                emailContent={emailContent}
                isGenerating={isGenerating}
                isGeneratingEmail={isGeneratingEmail}
                generatedEmail={generatedEmail}
                onPreview={onPreview}
                handleEditClick={() => {}}
                handleSaveTemplateClick={handleSaveTemplateClick}
                streamingData={streamingData}
                streamingText={streamingText}
                currentSection={currentSection}
                showActionButtons={false}
              />
            </Box>
          )}
          
          {activeTab === 1 && generatedEmail && (
            <Box sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              width: '100%',
              flex: 1
            }}>
              {/* Edit/Preview Toggle Header */}
              <Box sx={{ 
                p: 2, 
                borderBottom: 1, 
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'background.paper',
                flexShrink: 0,
                width: '100%'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {isEditModeInGeneratedTab ? 'Edit Email' : 'Email Preview'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {isEditModeInGeneratedTab && (
                    <Tooltip title="Reset to Original">
                      <IconButton
                        onClick={handleResetToOriginal}
                        disabled={!originalGeneratedHtml}
                        sx={{ opacity: originalGeneratedHtml ? 1 : 0.5 }}
                        size="small"
                      >
                        <Refresh fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={isEditModeInGeneratedTab ? "Switch to Preview" : "Switch to Edit"}>
                    <IconButton
                      onClick={handleEditPreviewToggle}
                      sx={{ 
                        bgcolor: isEditModeInGeneratedTab ? 'transparent' : 'secondary.main',
                        color: isEditModeInGeneratedTab ? 'secondary.main' : 'white',
                        border: isEditModeInGeneratedTab ? '1px solid' : 'none',
                        borderColor: isEditModeInGeneratedTab ? 'secondary.main' : 'transparent',
                        '&:hover': {
                          bgcolor: isEditModeInGeneratedTab ? 'secondary.main' : 'secondary.dark',
                          color: 'white'
                        }
                      }}
                    >
                      {isEditModeInGeneratedTab ? <Visibility /> : <Edit />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Save as Template">
                    <IconButton
                      onClick={handleSaveTemplateClick}
                      sx={{ 
                        bgcolor: '#0F2A52',
                        color: 'white',
                        '&:hover': { 
                          bgcolor: '#0a1f3a',
                          color: 'white'
                        }
                      }}
                    >
                      <Save />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Email as HTML">
                    <IconButton
                      onClick={handleDownloadEmail}
                      disabled={!generatedEmail?.html}
                      sx={{ 
                        bgcolor: 'primary.main',
                        color: 'white',
                        opacity: generatedEmail?.html ? 1 : 0.5,
                        '&:hover': { 
                          bgcolor: 'primary.dark',
                          color: 'white'
                        }
                      }}
                    >
                      <Download />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Content Area */}
              <Box sx={{ 
                flex: 1, 
                minHeight: 0, // Allow flex shrinking
                overflow: 'hidden', 
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {isEditModeInGeneratedTab ? (
                  <Box sx={{ 
                    flex: 1, 
                    minHeight: 0, // Allow flex shrinking
                    overflow: 'hidden', // Let iframe handle scrolling
                    p: 2,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <InPlaceEditableEmailPreviewMAIQ
                      htmlContent={editableHtmlContent}
                      onHtmlChange={handleHtmlChange}
                      isEditable={true}
                    />
                  </Box>
                ) : (
                  <Box sx={{ 
                    flex: 1, 
                    overflow: 'auto', 
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                    width: '100%'
                  }}>
                    <iframe
                      srcDoc={generatedEmail.html}
                      style={{
                        width: '100%',
                        // maxWidth: '600px',
                        minHeight: '400px',
                        height: 'calc(70vh - 300px)',
                        border: 'none',
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                        lineHeight: 'inherit',
                        backgroundColor: 'transparent',
                      }}
                      sandbox="allow-same-origin"
                      title="Email Preview"
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>


      {/* Save Template Modal using ModalMAIQ */}
      <ModalMAIQ
        open={showSaveTemplateModal}
        onClose={handleCloseSaveTemplateModal}
        customStyle={{
          width: '100%',
          maxWidth: '90vw',
          maxHeight: '95vh',
          backgroundColor: '#ffffff',
          borderRadius: 0,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            p: 2,
            backgroundColor: '#0F2A52',
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Typography variant="h6" fontWeight="600">
              Save Email Template
            </Typography>
            <Box>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={handleCloseSaveTemplateModal}
                  sx={{
                    color: 'white',
                    p: 0.5,
                  }}
                  size="small"
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
        
        {/* Content area for template form - scrollable */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          p: 3
        }}>
          <Step1Container
            templateState={templateState}
            setTemplateState={setTemplateState}
            error={templateFormError}
            html={false}
          />
        </Box>
        
        {/* Action buttons - fixed at bottom */}
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'flex-end',
          backgroundColor: 'white',
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          zIndex: 9,
          flexShrink: 0,
          gap: 2
        }}>
          <Button
            variant="outlined"
            onClick={handleCloseSaveTemplateModal}
            disabled={templateLoading}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 600,
              color: 'text.secondary',
              borderColor: 'divider',
              py: 0.75,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={templateLoading}
            startIcon={templateLoading ? <CircularProgress size={16} color="inherit" /> : <Save />}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 600, 
              bgcolor: '#0F2A52',
              color: 'white',
              py: 0.75,
              px: 3,
              '&:hover': {
                bgcolor: '#0a1f3a',
              }
            }}
          >
            {templateLoading ? 'Saving...' : 'Save Template'}
          </Button>
        </Box>
      </ModalMAIQ>

      {/* Result Modal (Success/Error) */}
      <ModalMAIQ
        open={showResultModal}
        onClose={handleResultModalClose}
        customStyle={{
          width: '500px',
          maxWidth: '90vw',
          backgroundColor: '#ffffff',
          borderRadius: 0,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header - matching existing theme */}
        <Box
          sx={{
            p: 2,
            backgroundColor: '#0F2A52', // Same as other modals
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {resultModalData.type === 'success' ? (
                <CheckCircle fontSize="small" />
              ) : (
                <Error fontSize="small" />
              )}
              <Typography variant="h6" fontWeight="600">
                {resultModalData.title}
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={handleResultModalClose}
                  sx={{
                    color: 'white',
                    p: 0.5,
                  }}
                  size="small"
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
        
        {/* Content area */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          p: 3,
          textAlign: 'center'
        }}>
          {/* Icon */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            {resultModalData.type === 'success' ? (
              <CheckCircle sx={{ fontSize: '64px', color: '#4caf50' }} />
            ) : (
              <Error sx={{ fontSize: '64px', color: '#f44336' }} />
            )}
          </Box>
          
          {/* Message */}
          <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
            {resultModalData.message}
          </Typography>
          
          {resultModalData.type === 'success' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Would you like to view all your saved templates?
            </Typography>
          )}
          
          {/* Action buttons - full width */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            p: 2,
            mt: 3,
            mx: -3,
            mb: -3
          }}>
            {resultModalData.type === 'success' ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleResultModalClose}
                  fullWidth
                  sx={{ 
                    textTransform: 'none', 
                    fontWeight: 600,
                    color: 'text.secondary',
                    borderColor: 'divider',
                    py: 0.75,
                    px: 3
                  }}
                >
                  Stay Here
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRedirectToTemplates}
                  startIcon={<ListAlt />}
                  fullWidth
                  sx={{ 
                    textTransform: 'none', 
                    fontWeight: 600, 
                    bgcolor: '#0F2A52',
                    color: 'white',
                    py: 0.75,
                    px: 3,
                    '&:hover': {
                      bgcolor: '#0a1f3a',
                    }
                  }}
                >
                  View All Templates
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleResultModalClose}
                fullWidth
                sx={{ 
                  textTransform: 'none', 
                  fontWeight: 600, 
                  bgcolor: '#0F2A52',
                  color: 'white',
                  py: 0.75,
                  px: 3,
                  '&:hover': {
                    bgcolor: '#0a1f3a',
                  }
                }}
              >
                Close
              </Button>
            )}
          </Box>
        </Box>
      </ModalMAIQ>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Download Modal using DownloadMAIQ */}
      <DownloadModalMAIQ
        open={showDownloadModal}
        onClose={handleCloseDownloadModal}
        downloadUrl={downloadUrl}
        title="Download Generated Email"
        fileName={downloadFileName}
      />

    </PreviewContainer>
  );
};

export default EmailContentPreview;
